import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '../../Servicios/supabaseClient.js';
import { qnCorePost } from '../../Servicios/qnCore.js';
import { Button, Icon, Stat, StatusPill } from '../../Components/ui/ui.jsx';
import { downloadInvoice } from './storage.js';
import { formatDate, formatMoney, formatTime, invoiceTone } from './format.js';

const PAGE_SIZE = 10;

const TABS = [
    { id: 'todas', label: 'Todas' },
    { id: 'completada', label: 'Completadas' },
    { id: 'procesando', label: 'Procesando' },
    { id: 'error', label: 'Con error' },
];

function statusGroup(status) {
    if (status === 'completada' || status === 'error') return status;
    return 'procesando';
}

function currentMonthValue() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function Facturas() {
    const { user, notify } = useOutletContext();
    const [searchParams, setSearchParams] = useSearchParams();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(currentMonthValue());
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);

    const [ncBusy, setNcBusy] = useState({});
    const ncInFlight = useRef(new Set());

    const filter = TABS.some((t) => t.id === searchParams.get('estado')) ? searchParams.get('estado') : 'todas';

    const fetchInvoices = useCallback(async () => {
        const { data, error } = await supabase
            .from('invoices')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error cargando facturas:', error.message);
            notify('No pudimos cargar tus facturas. Recargá la página.', 'danger');
        } else {
            setInvoices(data || []);
        }
        setLoading(false);
    }, [user, notify]);

    useEffect(() => {
        fetchInvoices();
        // Tiempo real: cuando n8n termina una factura, la lista se actualiza sola
        const channel = supabase
            .channel('dashboard:invoices')
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'invoices', filter: `user_id=eq.${user.id}` },
                () => fetchInvoices())
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [user, fetchInvoices]);

    const emitirNotaCredito = async (inv) => {
        if (ncInFlight.current.has(inv.id)) return;
        ncInFlight.current.add(inv.id);
        setNcBusy((b) => ({ ...b, [inv.id]: true }));
        try {
            const pre = await qnCorePost('/notas-credito/preview', { invoiceId: inv.id });
            if (!pre.ok) {
                notify(pre.data?.error || 'No pudimos preparar la nota de crédito.', 'danger');
                return;
            }
            const p = pre.data;
            const nro = `${String(p.pv).padStart(5, '0')}-${String(p.numero).padStart(8, '0')}`;
            const dialog = {
                title: 'Nota de crédito',
                text: `Vas a anular la factura ${nro} por ${formatMoney(p.totalCents / 100)}. Se emite una Nota de Crédito en AFIP y no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Anular factura',
                cancelButtonText: 'Cancelar',
                focusCancel: true,
            };
            if (p.esFce) {
                Object.assign(dialog, {
                    input: 'radio',
                    inputOptions: { N: 'No', S: 'Sí' },
                    inputValue: 'N',
                    inputLabel: '¿El cliente rechazó esta factura en el Registro de FCE?',
                });
            }
            const result = await Swal.fire(dialog);
            if (!result.isConfirmed) return;
            const body = { invoiceId: inv.id };
            if (p.esFce) body.anulacionFce = result.value === 'S' ? 'S' : 'N';
            const res = await qnCorePost('/notas-credito', body);
            if (res.status === 202) {
                notify('La nota de crédito quedó en verificación con AFIP. Avisale a QuantumNodo.', 'warn');
            } else if (res.ok) {
                notify('Nota de crédito emitida', 'ok');
            } else {
                notify(res.data?.error || 'No pudimos emitir la nota de crédito.', 'danger');
            }
            fetchInvoices();
        } finally {
            ncInFlight.current.delete(inv.id);
            setNcBusy((b) => ({ ...b, [inv.id]: false }));
        }
    };

    useEffect(() => { setPage(0); }, [filter, month, query]);

    const inMonth = useMemo(() => {
        if (!month) return invoices;
        const [y, m] = month.split('-').map(Number);
        return invoices.filter((inv) => {
            const d = new Date(inv.created_at);
            return d.getFullYear() === y && d.getMonth() + 1 === m;
        });
    }, [invoices, month]);

    const counts = useMemo(() => {
        const c = { todas: inMonth.length, completada: 0, procesando: 0, error: 0 };
        let billed = 0;
        inMonth.forEach((inv) => {
            const g = statusGroup(inv.status);
            c[g]++;
            if (g === 'completada') billed += Number(inv.monto) || 0;
        });
        return { ...c, billed };
    }, [inMonth]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return inMonth.filter((inv) => {
            if (filter !== 'todas' && statusGroup(inv.status) !== filter) return false;
            if (!q) return true;
            return [inv.concepto, inv.monto, inv.status].some((v) => String(v ?? '').toLowerCase().includes(q));
        });
    }, [inMonth, filter, query]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const rows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const monthName = month
        ? new Date(`${month}-01T00:00:00`).toLocaleString('es-AR', { month: 'long', year: 'numeric' })
        : 'todos los meses';

    const setFilter = (id) => {
        const next = new URLSearchParams(searchParams);
        if (id === 'todas') next.delete('estado');
        else next.set('estado', id);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="page">
            <header className="pageHeader">
                <div className="pageHeaderText">
                    <h1 className="pageTitle">Facturas</h1>
                    <p className="pageSubtitle">Todo lo que emitieron tus flujos. La lista se actualiza sola cuando una factura termina.</p>
                </div>
                <Link to="/dashboard/exportar" className="qn-btn qn-btn-ghost qn-btn-md">
                    <Icon name="download" size={16} />
                    Exportar ventas (RG 3685)
                </Link>
            </header>

            <section aria-label={`Resumen de ${monthName}`} className="grid4">
                <Stat value={loading ? '…' : formatMoney(counts.billed)} label={`Facturado en ${monthName}`} />
                <Stat value={loading ? '…' : counts.completada} label="Completadas" />
                <Stat
                    value={loading ? '…' : counts.procesando}
                    label="Procesando"
                    hint={counts.procesando > 0 ? 'Se completan solas en unos minutos' : undefined}
                />
                <Stat
                    value={loading ? '…' : counts.error}
                    label="Con error"
                    hint={counts.error > 0 ? 'Revisalas en el filtro "Con error"' : undefined}
                />
            </section>

            <section className="qn-card">
                <div className="toolbar">
                    <div className="segmented" role="group" aria-label="Filtrar por estado">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className="segBtn"
                                aria-pressed={filter === t.id}
                                onClick={() => setFilter(t.id)}
                            >
                                {t.label}
                                <span className="segCount">{counts[t.id]}</span>
                            </button>
                        ))}
                    </div>
                    <div className="toolbarRight">
                        <label className="searchBox">
                            <Icon name="search" size={16} />
                            <span className="srOnly">Buscar factura</span>
                            <input
                                type="search"
                                placeholder="Buscar por concepto o monto"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </label>
                        <label className="srOnly" htmlFor="facturas-mes">Mes</label>
                        <input
                            id="facturas-mes"
                            type="month"
                            className="qn-input monthInput"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                        />
                    </div>
                </div>

                <div className="tableWrap">
                    <table className="dataTable">
                        <thead>
                            <tr>
                                <th style={{ width: 150 }}>Fecha</th>
                                <th>Concepto</th>
                                <th className="right" style={{ width: 200 }}>Monto</th>
                                <th style={{ width: 160 }}>Estado</th>
                                <th style={{ width: 280 }}><span className="srOnly">Acción</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="emptyRow">
                                        {loading ? 'Cargando facturas…'
                                            : invoices.length === 0 ? 'Todavía no se generó ninguna factura.'
                                                : 'No hay facturas con estos filtros.'}
                                    </td>
                                </tr>
                            ) : rows.map((inv) => {
                                const st = invoiceTone(inv.status, inv);
                                return (
                                    <tr key={inv.id}>
                                        <td className="nowrap">
                                            <div className="cellStack">
                                                <span className="num">{formatDate(inv.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                                <span className="cellSub num">{formatTime(inv.created_at)}</span>
                                            </div>
                                        </td>
                                        <td>{inv.concepto || '-'}</td>
                                        <td className="right num nowrap strong">{formatMoney(inv.monto)}</td>
                                        <td><StatusPill tone={st.tone} title={inv.error || undefined}>{st.label}</StatusPill></td>
                                        <td>
                                            <div className="cellActions">
                                                {inv.status === 'completada' ? (
                                                    <>
                                                        <Button size="sm" onClick={() => downloadInvoice(inv.file_path, notify)}>Descargar PDF</Button>
                                                        {!inv.nc_de && !inv.anulada_at && (
                                                            <Button size="sm" disabled={!!ncBusy[inv.id]} onClick={() => emitirNotaCredito(inv)}>Nota de crédito</Button>
                                                        )}
                                                    </>
                                                ) : inv.status === 'error' ? (
                                                    <span className="cellSub" style={{ color: 'var(--danger)' }}>No se pudo emitir</span>
                                                ) : (
                                                    <span className="cellSub">Generando…</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="tableFooter">
                    <span>Mostrando {rows.length} de {filtered.length} · {monthName}</span>
                    <div className="pager">
                        <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                        <span className="num" style={{ padding: '0 8px' }}>Página {page + 1} de {pageCount}</span>
                        <Button size="sm" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
